import React from 'react';
import { 
  Box, 
  FormControlLabel, 
  Radio, 
  RadioGroup, 
  Stack, 
  Typography 
} from '@mui/material';
import { BaseField } from './BaseField';
import type { BaseFieldProps } from './types';

/**
 * Likert scale field component
 * Only available in form-builder format
 */
export const LikertField: React.FC<BaseFieldProps> = ({
  field,
  value = '',
  onChange,
  onBlur,
  mode = 'edit',
  error,
  touched,
  sx,
  showLabel = true,
  disabled,
}) => {
  // Extract field properties - Likert is only in form-builder format
  const label = 'label' in field ? field.label : '';
  const scale = 'likert_scale' in field && field.likert_scale ? field.likert_scale :
                 'scale' in field && field.scale ? field.scale : 5;
  const minLabel = 'min_label' in field && field.min_label ? field.min_label :
                   'minLabel' in field && field.minLabel ? field.minLabel : 'Strongly Disagree';
  const maxLabel = 'max_label' in field && field.max_label ? field.max_label :
                   'maxLabel' in field && field.maxLabel ? field.maxLabel : 'Strongly Agree';
  
  const options = Array.from({ length: scale }, (_, i) => (i + 1).toString());

  // Handle display mode
  if (mode === 'display') {
    return (
      <BaseField field={field} mode={mode} sx={sx} showLabel={showLabel}>
        <Typography variant="body2">
          {value ? `${value} out of ${scale}` : '-'}
        </Typography>
      </BaseField>
    );
  }

  // Handle edit/preview modes
  return (
    <BaseField
      field={field}
      mode={mode}
      error={error}
      touched={touched}
      sx={sx}
      showLabel={showLabel}
      disabled={disabled}
    >
      <Box>
        <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {minLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {maxLabel}
          </Typography>
        </Stack>
        <RadioGroup
          row
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          onBlur={onBlur}
          aria-label={label}
          name={field.id}
          sx={{ justifyContent: 'space-between' }}
        >
          {options.map((optionValue) => (
            <FormControlLabel
              key={optionValue}
              value={optionValue}
              control={<Radio size="small" />}
              label={optionValue}
              disabled={disabled || mode === 'preview'}
              sx={{ mx: 0.5 }}
            />
          ))}
        </RadioGroup>
      </Box>
    </BaseField>
  );
};